<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { format, fromUnixTime } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import InstagramConnectionsAPI from 'dashboard/api/instagramConnections';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

// Quanto tempo o rótulo "Copiado!" fica no lugar de "Copiar".
const COPY_FEEDBACK_MS = 2000;

// O passo 1 manda abrir o painel da Meta: é link de verdade, não texto solto,
// porque digitar o endereço à mão é onde a pessoa erra e cai num site clone.
const META_DEVELOPERS_URL = 'https://developers.facebook.com';

// Primeiro passo mostrado na reconexão. Ela reaproveita o mesmo app e o mesmo
// webhook — só o token é novo —, então os passos 1 a 6 (criar app, configurar
// webhook) levariam a pessoa a criar um SEGUNDO app, e o token gerado lá é
// recusado por não bater com o app_id da caixa.
const RECONNECT_FIRST_STEP = 7;

// Modo reconexão: a caixa já existe e só as credenciais mudam. O id vem pela
// query (mesmo padrão da tela de WhatsApp) e é o id da INBOX — que é
// exatamente o que a listagem devolve e o que a rota do backend espera.
const inboxId = Number(route.query.inbox_id) || null;
const isReconnecting = !!inboxId;

const webhookUrl = ref('');
const verifyToken = ref('');
const setupError = ref(false);
const setupLoading = ref(true);
const copiedField = ref('');

// Só existe em modo reconexão, para a tela dizer QUAL caixa está sendo
// reconectada. Fica vazio se a busca falhar: é contexto, não pré-requisito.
const inboxName = ref('');

const name = ref('');
const pageId = ref('');
const pageAccessToken = ref('');
const appId = ref('');
const appSecret = ref('');
// Chave do app do INSTAGRAM — é outra, e é com ela que a Meta assina os avisos
// que chegam. Sem ela, nada entra na caixa.
const instagramAppSecret = ref('');
const showInstagramAppSecret = ref(false);
// Token e chave secreta são valores COLADOS, de ~200 caracteres: escondidos
// atrás de bolinhas, uma colagem truncada passa despercebida e o erro que
// volta da Meta parece um problema de credencial.
const showPageAccessToken = ref(false);
const showAppSecret = ref(false);

const fieldErrors = ref({});
const submitError = ref('');
const isSubmitting = ref(false);
// Preenchido só no sucesso COM ressalva (inscrição não confirmada ou acesso
// com prazo). Sem ressalva a tela vai direto para a lista.
const warnings = ref([]);

let copyTimer = null;
// Uma resposta que chega depois do componente sair de cena não pode mexer em
// estado nem navegar — é o que sobra de "requisição pendente" numa SPA.
let isUnmounted = false;

const title = computed(() =>
  isReconnecting
    ? t('INSTAGRAM_CONNECT.CONNECT.TITLE_RECONNECT')
    : t('INSTAGRAM_CONNECT.CONNECT.TITLE')
);

// Sem o nome da caixa (a busca falhou) o texto de reconexão viraria
// `A caixa "" continua a mesma` — pior que não dizer nada. Cai no genérico.
const subtitle = computed(() =>
  isReconnecting && inboxName.value
    ? t('INSTAGRAM_CONNECT.CONNECT.SUBTITLE_RECONNECT', {
        name: inboxName.value,
      })
    : t('INSTAGRAM_CONNECT.CONNECT.SUBTITLE')
);

const submitLabel = computed(() =>
  isReconnecting
    ? t('INSTAGRAM_CONNECT.CONNECT.FORM.SUBMIT_RECONNECT')
    : t('INSTAGRAM_CONNECT.CONNECT.FORM.SUBMIT')
);

const successTitle = computed(() =>
  isReconnecting
    ? t('INSTAGRAM_CONNECT.SUCCESS.TITLE_RECONNECT')
    : t('INSTAGRAM_CONNECT.SUCCESS.TITLE')
);

const goToIndex = () => router.push({ name: 'instagram_index' });

const copyValue = async (field, value) => {
  if (!value) return;

  try {
    await copyTextToClipboard(value);
    // O temporizador é agendado DEPOIS do await: sem esta guarda ele escaparia
    // da limpeza do onUnmounted quando a tela é fechada durante a cópia.
    if (isUnmounted) return;
    copiedField.value = field;
    clearTimeout(copyTimer);
    copyTimer = setTimeout(() => {
      copiedField.value = '';
    }, COPY_FEEDBACK_MS);
  } catch (error) {
    // Área de transferência bloqueada (o Chatwell roda embarcado em iframe,
    // onde isso acontece de verdade): sem aviso, a pessoa acha que o botão
    // quebrou e digita o valor à mão — que é justamente o que ele evita.
    if (isUnmounted) return;
    copiedField.value = '';
    useAlert(t('INSTAGRAM_CONNECT.ACTIONS.COPY_ERROR'));
  }
};

const copyLabel = field =>
  copiedField.value === field
    ? t('INSTAGRAM_CONNECT.ACTIONS.COPIED')
    : t('INSTAGRAM_CONNECT.ACTIONS.COPY');

const toggleLabel = visible =>
  visible
    ? t('INSTAGRAM_CONNECT.ACTIONS.HIDE')
    : t('INSTAGRAM_CONNECT.ACTIONS.SHOW');

// Mapa explícito em vez de chave dinâmica: um código novo no backend cairia
// numa chave inexistente e vazaria "INSTAGRAM_CONNECT.ERRORS.FOO" na tela.
// Aqui um código desconhecido cai no texto genérico.
const errorMessageForCode = (code, missingScopes) => {
  switch (code) {
    case 'invalid_token':
      return t('INSTAGRAM_CONNECT.ERRORS.INVALID_TOKEN');
    case 'invalid_app_credentials':
      return t('INSTAGRAM_CONNECT.ERRORS.INVALID_APP_CREDENTIALS');
    case 'page_not_found':
      return t('INSTAGRAM_CONNECT.ERRORS.PAGE_NOT_FOUND');
    case 'token_page_mismatch':
      return t('INSTAGRAM_CONNECT.ERRORS.TOKEN_PAGE_MISMATCH');
    case 'no_instagram_linked':
      return t('INSTAGRAM_CONNECT.ERRORS.NO_INSTAGRAM_LINKED');
    case 'token_expires':
      return t('INSTAGRAM_CONNECT.ERRORS.TOKEN_EXPIRES');
    case 'missing_permission':
      // A lista de permissões que faltam é o que torna esta mensagem
      // acionável; sem ela, o texto genérico já cita as três exigidas.
      return missingScopes?.length
        ? t('INSTAGRAM_CONNECT.ERRORS.MISSING_PERMISSION', {
            scopes: missingScopes.join(', '),
          })
        : t('INSTAGRAM_CONNECT.ERRORS.MISSING_PERMISSION_GENERIC');
    case 'already_connected':
      return t('INSTAGRAM_CONNECT.ERRORS.ALREADY_CONNECTED');
    // Reconexão com credenciais de OUTRO perfil: código próprio do backend,
    // porque "já conectado em outra caixa" mandaria procurar uma duplicata
    // que não existe.
    case 'reconnect_different_account':
      return t('INSTAGRAM_CONNECT.ERRORS.RECONNECT_DIFFERENT_ACCOUNT');
    case 'meta_unavailable':
      return t('INSTAGRAM_CONNECT.ERRORS.META_UNAVAILABLE');
    default:
      return '';
  }
};

// Falha de rede não devolve corpo nem status nenhum — cai no texto genérico
// junto com qualquer 500 inesperado, em vez de deixar o botão "carregando"
// sem explicação.
const errorMessage = error => {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const byCode = errorMessageForCode(data?.error_code, data?.missing_scopes);

  if (byCode) return byCode;
  if (status === 404) return t('INSTAGRAM_CONNECT.ERRORS.INBOX_NOT_FOUND');
  // 401 tem DUAS origens muito diferentes, e tratá-las com o mesmo texto já
  // custou caro: a sessão expirada apareceu para o dono da conta como "só
  // administradores podem conectar", e ele foi procurar um problema de permissão
  // que não existia (ele é administrador). Sessão expirada é o caso comum — o
  // cadastro leva ~40 min e a aba costuma ficar aberta esse tempo todo.
  //
  // O `authenticate_user!` do devise responde no formato { errors: [...] }; a
  // recusa por papel não traz esse campo.
  if (status === 401) {
    return data?.errors
      ? t('INSTAGRAM_CONNECT.ERRORS.SESSION_EXPIRED')
      : t('INSTAGRAM_CONNECT.ERRORS.NOT_ADMIN');
  }
  // O Rack::Attack corta as tentativas por hora e responde 429 sem corpo. Cair
  // no texto de rede aqui manda a pessoa reiniciar o roteador por uma hora.
  if (status === 429) return t('INSTAGRAM_CONNECT.ERRORS.RATE_LIMITED');
  return t('INSTAGRAM_CONNECT.ERRORS.NETWORK');
};

const validate = () => {
  const errors = {};

  if (!isReconnecting) {
    if (!name.value.trim()) errors.name = true;
    if (!pageId.value.trim()) errors.pageId = true;
  }
  if (!pageAccessToken.value.trim()) errors.pageAccessToken = true;
  if (!appId.value.trim()) errors.appId = true;
  if (!appSecret.value.trim()) errors.appSecret = true;
  if (!instagramAppSecret.value.trim()) errors.instagramAppSecret = true;

  fieldErrors.value = errors;
  return !Object.keys(errors).length;
};

// A caixa foi criada/atualizada de qualquer forma: `subscribed: false` e um
// prazo de acesso não desfazem nada, só exigem uma ação a mais do cliente no
// painel da Meta. Por isso viram aviso na tela em vez de erro — e a tela
// segura a navegação até a pessoa ler.
// O backend manda o instante em segundos (o relógio da Meta). "Tem prazo" sem
// dizer qual data não dá o que fazer — com a data, a pessoa anota na agenda.
// dd/MM/yyyy, não o formato em inglês do resto do produto: esta data é lida
// pelo dono da clínica, que precisa anotar o prazo na agenda.
const formatExpiresAt = seconds => format(fromUnixTime(seconds), 'dd/MM/yyyy');

const collectWarnings = data => {
  const collected = [];

  if (data?.subscribed === false) {
    // Na reconexão o texto de criação mandaria a pessoa "voltar à lista e
    // clicar em Reconectar" — o que ela acabou de fazer.
    collected.push(
      t(
        isReconnecting
          ? 'INSTAGRAM_CONNECT.SUCCESS.NOT_SUBSCRIBED_RECONNECT'
          : 'INSTAGRAM_CONNECT.SUCCESS.NOT_SUBSCRIBED'
      )
    );
  }
  if (data?.data_access_expires_at) {
    collected.push(
      t('INSTAGRAM_CONNECT.SUCCESS.DATA_ACCESS_EXPIRES', {
        date: formatExpiresAt(data.data_access_expires_at),
      })
    );
  }

  return collected;
};

const submit = async () => {
  submitError.value = '';
  if (!validate()) return;

  isSubmitting.value = true;
  try {
    // Na reconexão o backend usa o page_id do canal e ignora o nome: mandar
    // esses campos aqui só serviria para apontar a caixa para outra página.
    const payload = {
      page_access_token: pageAccessToken.value.trim(),
      app_id: appId.value.trim(),
      app_secret: appSecret.value.trim(),
      instagram_app_secret: instagramAppSecret.value.trim(),
    };
    const { data } = isReconnecting
      ? await InstagramConnectionsAPI.reconnect(inboxId, payload)
      : await InstagramConnectionsAPI.create({
          ...payload,
          // O backend lê `inbox_name` (params.permit), não `name`.
          inbox_name: name.value.trim(),
          page_id: pageId.value.trim(),
        });

    if (isUnmounted) return;

    warnings.value = collectWarnings(data);
    // Sem ressalva a tela some e vai para a lista. Depois de 40 minutos no
    // painel da Meta, sair calado se lê como falha: o alerta é a única
    // confirmação de que deu certo. Com ressalva, o painel já confirma — e
    // dizer "está conectado" ao lado de "nada vai chegar aqui" se contradiz.
    if (!warnings.value.length) {
      useAlert(
        t(
          isReconnecting
            ? 'INSTAGRAM_CONNECT.SUCCESS.ALERT_RECONNECT'
            : 'INSTAGRAM_CONNECT.SUCCESS.ALERT'
        )
      );
      goToIndex();
    }
  } catch (error) {
    if (isUnmounted) return;
    submitError.value = errorMessage(error);
  } finally {
    if (!isUnmounted) isSubmitting.value = false;
  }
};

const loadSetup = async () => {
  try {
    const { data } = await InstagramConnectionsAPI.setup();
    if (isUnmounted) return;
    // Valor em branco é falha, não sucesso: o código de verificação não tem
    // padrão na instalação, então numa instância onde ninguém o configurou a
    // resposta vem 200 com string vazia. Sem esta checagem a tela mostrava dois
    // blocos vazios com um botão Copiar que não faz nada, a pessoa colava vazio
    // no painel da Meta e passava 40 minutos em círculo — o próprio passo 5
    // mandando "confira se colou os dois valores inteiros".
    if (!data.webhook_url || !data.verify_token) {
      setupError.value = true;
      return;
    }
    webhookUrl.value = data.webhook_url;
    verifyToken.value = data.verify_token;
    setupError.value = false;
  } catch (error) {
    if (!isUnmounted) setupError.value = true;
  } finally {
    if (!isUnmounted) setupLoading.value = false;
  }
};

// Só para escrever o nome da caixa no cabeçalho da reconexão. Falha aqui é
// silenciosa de propósito: o formulário funciona sem esse nome.
const loadInboxName = async () => {
  try {
    const { data } = await InstagramConnectionsAPI.get();
    if (isUnmounted) return;
    inboxName.value =
      data.find(connection => connection.id === inboxId)?.name || '';
  } catch (error) {
    if (!isUnmounted) inboxName.value = '';
  }
};

onMounted(() => {
  // Na reconexão o webhook já está configurado e os dois valores copiáveis não
  // aparecem — buscar o `setup` seria uma requisição sem destino na tela.
  if (isReconnecting) {
    loadInboxName();
    return;
  }
  loadSetup();
});

onUnmounted(() => {
  isUnmounted = true;
  clearTimeout(copyTimer);
});
</script>

<template>
  <div class="w-full h-full p-8 overflow-auto">
    <div class="max-w-5xl">
      <h1 class="text-xl font-medium text-n-slate-12">{{ title }}</h1>
      <p class="mt-1 mb-6 text-sm text-n-slate-11">{{ subtitle }}</p>

      <div
        v-if="warnings.length"
        class="flex flex-col items-start max-w-2xl gap-3 p-4 border rounded-lg border-n-weak bg-n-solid-2"
      >
        <p class="font-medium text-n-slate-12">{{ successTitle }}</p>
        <p
          v-for="warning in warnings"
          :key="warning"
          class="text-sm text-n-amber-11"
        >
          {{ warning }}
        </p>
        <NextButton
          solid
          teal
          :label="$t('INSTAGRAM_CONNECT.ACTIONS.VIEW_LIST')"
          @click="goToIndex"
        />
      </div>

      <div v-else class="grid gap-8 lg:grid-cols-2">
        <section class="flex flex-col gap-4">
          <h2 class="text-base font-medium text-n-slate-12">
            {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.TITLE') }}
          </h2>

          <!-- Reconexão reaproveita o app e o webhook que já existem. Sem este
               aviso a pessoa segue o tutorial do começo, cria um SEGUNDO app e
               o token que sai de lá é recusado. -->
          <p
            v-if="isReconnecting"
            class="p-3 text-sm border rounded-lg text-n-amber-11 border-n-amber-6 bg-n-alpha-2"
          >
            {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.RECONNECT_NOTICE') }}
          </p>

          <!-- Pré-requisitos que não se resolvem no meio do caminho (tipo de
               conta, vínculo com a Página, computador em vez de celular): em
               destaque acima dos passos, porque descobri-los no passo 4 custa
               a sessão inteira. -->
          <div
            v-else
            class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak bg-n-solid-2"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.BEFORE_TITLE') }}
            </p>
            <ul
              class="flex flex-col gap-1 pl-5 m-0 text-sm list-disc text-n-slate-11"
            >
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.BEFORE_1') }}</li>
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.BEFORE_2') }}</li>
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.BEFORE_3') }}</li>
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.BEFORE_4') }}</li>
            </ul>
          </div>

          <!-- `start` mantém a numeração real dos passos na reconexão (7 a 11):
               os textos citam "passos 8 a 10" e renumerar quebraria a
               referência. -->
          <div class="p-3 mb-4 rounded-lg bg-n-solid-2">
            <p class="mb-2 text-sm font-medium text-n-slate-12">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.PREREQ_TITLE') }}
            </p>
            <ul
              class="flex flex-col gap-1 pl-5 m-0 text-sm list-disc text-n-slate-11"
            >
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.PREREQ_1') }}</li>
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.PREREQ_2') }}</li>
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.PREREQ_3') }}</li>
              <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.PREREQ_4') }}</li>
            </ul>
          </div>

          <ol
            :start="isReconnecting ? RECONNECT_FIRST_STEP : 1"
            class="flex flex-col gap-2 pl-5 m-0 text-sm list-decimal text-n-slate-11"
          >
            <I18nT
              v-if="!isReconnecting"
              keypath="INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_1"
              tag="li"
            >
              <template #link>
                <a
                  :href="META_DEVELOPERS_URL"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="underline text-n-brand"
                >
                  {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_1_LINK') }}
                </a>
              </template>
            </I18nT>
            <li v-if="!isReconnecting">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_2') }}
            </li>
            <li v-if="!isReconnecting">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_3') }}
            </li>
            <li v-if="!isReconnecting">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_4') }}
            </li>
            <li v-if="!isReconnecting">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_5') }}
            </li>
            <li v-if="!isReconnecting">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_6') }}
            </li>
            <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_7') }}</li>
            <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_8') }}</li>
            <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_9') }}</li>
            <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_10') }}</li>
            <li>{{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_11') }}</li>
            <li v-if="!isReconnecting">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_12') }}
            </li>
            <!-- Fecho próprio da reconexão: o passo 12 manda "clicar em
                 Conectar", botão que não existe nesse modo. -->
            <li v-else>
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.STEP_RECONNECT_END') }}
            </li>
          </ol>

          <!-- Os dois valores só servem ao passo 5 (configurar o webhook), que
               a reconexão não mostra nem refaz. -->
          <template v-if="!isReconnecting">
            <p v-if="setupLoading" class="text-sm text-n-slate-11">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.SETUP_LOADING') }}
            </p>
            <p v-else-if="setupError" class="text-sm text-n-ruby-11">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.SETUP_ERROR') }}
            </p>
            <template v-else>
              <!-- Os dois valores que o cliente digitaria errado se tivesse que
                   digitar: aparecem prontos, com botão de cópia. O rótulo diz o
                   nome do campo NO PAINEL DA META, que é outro — é aí que trava. -->
              <p class="text-sm text-n-slate-11">
                {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.COPY_INTRO') }}
              </p>

              <div class="flex flex-col gap-1">
                <span class="text-sm text-n-slate-12">
                  {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.WEBHOOK_LABEL') }}
                </span>
                <div
                  class="flex items-center gap-2 px-3 py-2 border rounded-lg border-n-weak bg-n-solid-2"
                >
                  <code class="text-sm break-all text-n-slate-12">
                    {{ webhookUrl }}
                  </code>
                  <NextButton
                    ghost
                    slate
                    size="sm"
                    class="ml-auto shrink-0"
                    :label="copyLabel('webhook')"
                    @click="copyValue('webhook', webhookUrl)"
                  />
                </div>
                <span class="text-xs text-n-slate-10">
                  {{ $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.WEBHOOK_HINT') }}
                </span>
              </div>

              <div class="flex flex-col gap-1">
                <span class="text-sm text-n-slate-12">
                  {{
                    $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.VERIFY_TOKEN_LABEL')
                  }}
                </span>
                <div
                  class="flex items-center gap-2 px-3 py-2 border rounded-lg border-n-weak bg-n-solid-2"
                >
                  <code class="text-sm break-all text-n-slate-12">
                    {{ verifyToken }}
                  </code>
                  <NextButton
                    ghost
                    slate
                    size="sm"
                    class="ml-auto shrink-0"
                    :label="copyLabel('verifyToken')"
                    @click="copyValue('verifyToken', verifyToken)"
                  />
                </div>
                <span class="text-xs text-n-slate-10">
                  {{
                    $t('INSTAGRAM_CONNECT.CONNECT.TUTORIAL.VERIFY_TOKEN_HINT')
                  }}
                </span>
              </div>
            </template>
          </template>
        </section>

        <form class="flex flex-col gap-4" @submit.prevent="submit">
          <!-- Reconexão não pede nome nem página: a caixa e a página já são as
               que existem, e o backend ignora os dois campos aqui. -->
          <label v-if="!isReconnecting" class="text-sm text-n-slate-12">
            {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.NAME_LABEL') }}
            <input
              v-model="name"
              type="text"
              :placeholder="
                $t('INSTAGRAM_CONNECT.CONNECT.FORM.NAME_PLACEHOLDER')
              "
            />
            <span
              v-if="fieldErrors.name"
              role="alert"
              class="text-sm text-n-ruby-11"
            >
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.NAME_ERROR') }}
            </span>
          </label>

          <label v-if="!isReconnecting" class="text-sm text-n-slate-12">
            {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ID_LABEL') }}
            <input
              v-model="pageId"
              type="text"
              :placeholder="
                $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ID_PLACEHOLDER')
              "
            />
            <span class="text-xs text-n-slate-10">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ID_HINT') }}
            </span>
            <span
              v-if="fieldErrors.pageId"
              role="alert"
              class="text-sm text-n-ruby-11"
            >
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ID_ERROR') }}
            </span>
          </label>

          <label class="text-sm text-n-slate-12">
            {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ACCESS_TOKEN_LABEL') }}
            <div class="flex items-start gap-2">
              <input
                v-model="pageAccessToken"
                :type="showPageAccessToken ? 'text' : 'password'"
                autocomplete="new-password"
                data-lpignore="true"
                data-1p-ignore
                class="flex-1 min-w-0"
              />
              <NextButton
                type="button"
                ghost
                slate
                size="sm"
                class="shrink-0"
                :label="toggleLabel(showPageAccessToken)"
                @click="showPageAccessToken = !showPageAccessToken"
              />
            </div>
            <span class="text-xs text-n-slate-10">
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ACCESS_TOKEN_HINT') }}
            </span>
            <span
              v-if="fieldErrors.pageAccessToken"
              role="alert"
              class="text-sm text-n-ruby-11"
            >
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.PAGE_ACCESS_TOKEN_ERROR') }}
            </span>
          </label>

          <label class="text-sm text-n-slate-12">
            {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.APP_ID_LABEL') }}
            <input v-model="appId" type="text" />
            <span
              v-if="fieldErrors.appId"
              role="alert"
              class="text-sm text-n-ruby-11"
            >
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.APP_ID_ERROR') }}
            </span>
          </label>

          <label class="text-sm text-n-slate-12">
            {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.APP_SECRET_LABEL') }}
            <div class="flex items-start gap-2">
              <input
                v-model="appSecret"
                :type="showAppSecret ? 'text' : 'password'"
                autocomplete="new-password"
                data-lpignore="true"
                data-1p-ignore
                class="flex-1 min-w-0"
              />
              <NextButton
                type="button"
                ghost
                slate
                size="sm"
                class="shrink-0"
                :label="toggleLabel(showAppSecret)"
                @click="showAppSecret = !showAppSecret"
              />
            </div>
            <span
              v-if="fieldErrors.appSecret"
              role="alert"
              class="text-sm text-n-ruby-11"
            >
              {{ $t('INSTAGRAM_CONNECT.CONNECT.FORM.APP_SECRET_ERROR') }}
            </span>
          </label>

          <label class="text-sm text-n-slate-12">
            {{
              $t('INSTAGRAM_CONNECT.CONNECT.FORM.INSTAGRAM_APP_SECRET_LABEL')
            }}
            <div class="flex items-start gap-2">
              <input
                v-model="instagramAppSecret"
                :type="showInstagramAppSecret ? 'text' : 'password'"
                autocomplete="new-password"
                data-lpignore="true"
                data-1p-ignore
                class="flex-1 min-w-0"
              />
              <NextButton
                type="button"
                ghost
                slate
                size="sm"
                class="shrink-0"
                :label="toggleLabel(showInstagramAppSecret)"
                @click="showInstagramAppSecret = !showInstagramAppSecret"
              />
            </div>
            <span class="text-sm text-n-slate-11">
              {{
                $t('INSTAGRAM_CONNECT.CONNECT.FORM.INSTAGRAM_APP_SECRET_HINT')
              }}
            </span>
            <span
              v-if="fieldErrors.instagramAppSecret"
              role="alert"
              class="text-sm text-n-ruby-11"
            >
              {{
                $t('INSTAGRAM_CONNECT.CONNECT.FORM.INSTAGRAM_APP_SECRET_ERROR')
              }}
            </span>
          </label>

          <p v-if="submitError" role="alert" class="text-sm text-n-ruby-11">
            {{ submitError }}
          </p>

          <div class="flex flex-wrap gap-2">
            <NextButton
              type="submit"
              solid
              teal
              :is-loading="isSubmitting"
              :disabled="isSubmitting"
              :label="
                isSubmitting
                  ? $t('INSTAGRAM_CONNECT.CONNECT.FORM.SUBMITTING')
                  : submitLabel
              "
            />
            <NextButton
              type="button"
              outline
              slate
              :label="$t('INSTAGRAM_CONNECT.ACTIONS.BACK_TO_INDEX')"
              @click="goToIndex"
            />
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
